import { isOwnedMetricObservation, type MetricObservation } from './metrics'
import {
  type CanonicalIdPaths,
  normalizeError,
  redactSensitiveString,
  sanitizeLogValue,
  sanitizeOwnedLogValue,
  truncate,
} from './redaction'
import type {
  CanonicalLogEvent,
  LogFields,
  LogStatus,
  ObservabilityContext,
  ObservabilitySource,
  SafeLogValue,
} from './types'

const RESERVED_FIELDS = new Set([
  'timestamp',
  'status',
  'message',
  'service',
  'env',
  'version',
  'event',
  'outcome',
  'source',
  'durationMs',
  'duration_ms',
  'logger',
  'http',
  'workflow',
  'brew',
  'error',
])

/**
 * The envelope's correlation ids: the release commit SHA in `version` and the
 * request and Brew ids. A full SHA (40 hex characters), a Convex document id,
 * a Clerk `user_` id and a `createRequestId()` id are all 32+ character
 * alphanumeric runs, which the token rule scrubs, so an id-shaped value at
 * these paths is kept verbatim and stays searchable. Every `brew` key is an
 * id: the `satisfies` clause stops compiling until a new one is listed, and
 * rejects a key the envelope does not define.
 *
 * `workflow` is deliberately absent. A Workflow SDK run or step id (`wrun_` /
 * `step_` + a 26-character ULID) is 31 characters, below the token rule, so it
 * reads fine without an exemption, while a longer `workflow.run_id` can only
 * be foreign, e.g. a caller's W3C `baggage` entry.
 */
const CANONICAL_ID_PATHS = {
  version: true,
  http: { request_id: true },
  brew: {
    operation_id: true,
    org_id: true,
    brand_id: true,
    user_id: true,
    chat_id: true,
    email_id: true,
    automation_id: true,
    execution_id: true,
    send_id: true,
  },
} as const satisfies {
  version: true
  http: Partial<Record<keyof NonNullable<CanonicalLogEvent['http']>, true>>
  brew: Record<keyof NonNullable<CanonicalLogEvent['brew']>, true>
}

/** The shape of a `createRequestId()` id: `req_` + 32 hex characters. */
const MINTED_REQUEST_ID_PATTERN = /^req_[0-9a-f]{32}$/

/**
 * The id paths this event keeps verbatim. A request id outside the minted
 * shape may be the caller's own `x-request-id`, which the public API adopts
 * and copies into `brew.operation_id`, so no id path keeps that value:
 * `http.request_id` and every copy of it take the token rule. This is a shape
 * filter, not provenance: a caller can pin a `req_` + 32 hex id on purpose,
 * but no credential has that shape by accident.
 */
function canonicalIdPathsFor(event: CanonicalLogEvent): CanonicalIdPaths {
  const requestId = event.http?.request_id
  if (requestId === undefined || MINTED_REQUEST_ID_PATTERN.test(requestId)) {
    return CANONICAL_ID_PATHS
  }
  // Only the context groups can hold a copy of the request id. A top-level
  // id (`version`) comes from logger config, so a caller who pins the same
  // value as `x-request-id` cannot cost it the exemption.
  const kept: Record<string, CanonicalIdPaths | true> = {}
  for (const [key, path] of Object.entries(CANONICAL_ID_PATHS)) {
    kept[key] =
      path === true
        ? true
        : idPathsWithout(
            path,
            (event as Record<string, unknown>)[key],
            requestId
          )
  }
  return kept
}

/** `paths` minus every id path whose value in `values` is `value`. */
function idPathsWithout(
  paths: CanonicalIdPaths,
  values: unknown,
  value: string
): CanonicalIdPaths {
  const kept: Record<string, CanonicalIdPaths | true> = {}
  for (const [key, path] of Object.entries(paths)) {
    const entry =
      values && typeof values === 'object'
        ? (values as Record<string, unknown>)[key]
        : undefined
    if (path !== true) {
      kept[key] = idPathsWithout(path, entry, value)
    } else if (entry !== value) {
      kept[key] = true
    }
  }
  return kept
}

function definedObject<T extends Record<string, unknown>>(
  value: T
): Partial<T> | undefined {
  const entries = Object.entries(value).filter(
    ([, entry]) => entry !== undefined
  )
  return entries.length > 0
    ? (Object.fromEntries(entries) as Partial<T>)
    : undefined
}

export function workflowEventKey(args: {
  runId: string
  stepId: string
  attempt: number
  event: string
}): string {
  return `${args.runId}:${args.stepId}:${args.attempt}:${args.event}`
}

export function buildLogEvent(args: {
  status: LogStatus
  event: string
  message: string
  service: string
  env: string
  version?: string
  source: ObservabilitySource
  loggerName: string
  context?: ObservabilityContext
  fields?: LogFields
  error?: unknown
  now?: Date
}): CanonicalLogEvent {
  const context = args.context ?? {}
  const fields = args.fields ?? {}
  let normalizedError: ReturnType<typeof normalizeError> | undefined
  if (args.error !== undefined) {
    normalizedError = normalizeError(args.error)
  } else if (fields.error !== undefined) {
    normalizedError = normalizeError(fields.error)
  }
  const durationMs =
    typeof fields.durationMs === 'number' && Number.isFinite(fields.durationMs)
      ? fields.durationMs
      : undefined
  const outcome =
    typeof fields.outcome === 'string' ? fields.outcome : undefined

  const http = definedObject({
    request_id: context.requestId,
    route: context.route,
    method: context.method,
    status_code: context.statusCode,
  })
  const eventKey =
    context.workflowRunId &&
    context.workflowStepId &&
    context.workflowAttempt !== undefined
      ? workflowEventKey({
          runId: context.workflowRunId,
          stepId: context.workflowStepId,
          attempt: context.workflowAttempt,
          event: args.event,
        })
      : undefined
  const workflow = definedObject({
    name: context.workflowName,
    run_id: context.workflowRunId,
    step_id: context.workflowStepId,
    attempt: context.workflowAttempt,
    event_key: eventKey,
  })
  const brew = definedObject({
    operation_id: context.operationId,
    org_id: context.orgId,
    brand_id: context.brandId,
    user_id: context.userId,
    chat_id: context.chatId,
    email_id: context.emailId,
    automation_id: context.automationId,
    execution_id: context.executionId,
    send_id: context.sendId,
  })

  const customFields: Record<string, SafeLogValue> = {}
  for (const [key, value] of Object.entries(fields)) {
    if (!RESERVED_FIELDS.has(key)) {
      customFields[key] = sanitizeLogValue(value)
    }
  }

  return {
    timestamp: (args.now ?? new Date()).toISOString(),
    status: args.status,
    message: truncate(redactSensitiveString(args.message), 2000),
    service: args.service,
    env: args.env,
    ...(args.version ? { version: args.version } : {}),
    event: args.event,
    ...(outcome ? { outcome } : {}),
    source: args.source,
    ...(durationMs !== undefined ? { duration_ms: durationMs } : {}),
    logger: { name: args.loggerName },
    ...(http ? { http } : {}),
    ...(workflow ? { workflow } : {}),
    ...(brew ? { brew } : {}),
    ...(normalizedError ? { error: normalizedError } : {}),
    ...customFields,
  }
}

/** Internal metric emission path; all fields retain the canonical redaction rules. */
export function serializeMetricLogEvent(
  observation: MetricObservation,
  runtime: {
    service: string
    env: string
    version: string
    source: ObservabilitySource
    maxBytes: number
  }
): string {
  // Resolve every external getter before a scoped secret snapshot can be taken.
  const service = runtime.service
  const env = runtime.env
  const version = runtime.version === 'unknown' ? undefined : runtime.version
  const source = runtime.source
  const base = buildLogEvent({
    status: 'info',
    event: 'telemetry.metric',
    message: 'Custom metric observation',
    service,
    env,
    version,
    source,
    loggerName: 'metrics',
    context: observation.context,
  })
  const payload = {
    ...base,
    schema_version: observation.schema_version,
    metric: observation.metric,
  }
  // Preserve the generic path's ordering: its context/metric getters resolve
  // before the final budget getter, which may itself rotate a configured secret.
  const maxBytes = runtime.maxBytes
  const owned =
    isOwnedMetricObservation(observation) &&
    typeof service === 'string' &&
    typeof env === 'string' &&
    (version === undefined || typeof version === 'string') &&
    typeof source === 'string' &&
    typeof maxBytes === 'number' &&
    Number.isFinite(maxBytes)
  if (!owned) {
    return serializeLogEvent(payload, { maxBytes })
  }
  // Private packet provenance and primitive runtime fields prove there are no
  // caller getters left. This snapshot belongs only to this synchronous emit;
  // a console sink can rotate secrets before the next packet is serialized.
  return serializeSanitizedLogEvent(
    sanitizeOwnedLogValue(
      payload,
      canonicalIdPathsFor(payload)
    ) as CanonicalLogEvent,
    Math.max(1024, maxBytes)
  )
}

function encodedBytes(value: string): number {
  return new TextEncoder().encode(value).byteLength
}

export function serializeLogEvent(
  event: CanonicalLogEvent,
  options: { maxBytes?: number } = {}
): string {
  const maxBytes = Math.max(1024, options.maxBytes ?? 32 * 1024)
  const sanitized = sanitizeLogValue(
    event,
    canonicalIdPathsFor(event)
  ) as CanonicalLogEvent
  return serializeSanitizedLogEvent(sanitized, maxBytes)
}

function serializeSanitizedLogEvent(
  sanitized: CanonicalLogEvent,
  maxBytes: number
): string {
  const full = JSON.stringify(sanitized)
  const fullBytes = encodedBytes(full)
  if (fullBytes <= maxBytes) {
    return full
  }

  const compact: CanonicalLogEvent = {
    timestamp: sanitized.timestamp,
    status: sanitized.status,
    message: truncate(sanitized.message, 512),
    service: sanitized.service,
    env: sanitized.env,
    ...(sanitized.version ? { version: sanitized.version } : {}),
    event: sanitized.event,
    ...(sanitized.outcome ? { outcome: sanitized.outcome } : {}),
    source: sanitized.source,
    ...(sanitized.duration_ms !== undefined
      ? { duration_ms: sanitized.duration_ms }
      : {}),
    logger: sanitized.logger,
    ...(sanitized.http ? { http: sanitized.http } : {}),
    ...(sanitized.workflow ? { workflow: sanitized.workflow } : {}),
    ...(sanitized.brew ? { brew: sanitized.brew } : {}),
    ...(sanitized.error
      ? {
          error: {
            kind: sanitized.error.kind,
            message: truncate(sanitized.error.message, 512),
            ...(sanitized.error.code ? { code: sanitized.error.code } : {}),
            ...(sanitized.error.stack
              ? { stack: truncate(sanitized.error.stack, 1200) }
              : {}),
          },
        }
      : {}),
    log_truncated: true,
    original_bytes: fullBytes,
  }

  let serialized = JSON.stringify(compact)
  if (encodedBytes(serialized) <= maxBytes) {
    return serialized
  }

  if (compact.error) {
    delete compact.error.stack
  }
  compact.message = truncate(compact.message, 160)
  serialized = JSON.stringify(compact)
  if (encodedBytes(serialized) <= maxBytes) {
    return serialized
  }

  const terminalLine = (version: string | undefined): string =>
    JSON.stringify({
      timestamp: compact.timestamp,
      status: compact.status,
      message: '[Log exceeded serialization budget]',
      service: compact.service,
      env: compact.env,
      ...(version ? { version } : {}),
      event: compact.event,
      source: compact.source,
      logger: compact.logger,
      log_truncated: true,
      original_bytes: fullBytes,
    } satisfies CanonicalLogEvent)
  // The release SHA keeps even this line filterable by release, but only when
  // the line still fits: the version never costs the budget.
  const withVersion = terminalLine(compact.version)
  return encodedBytes(withVersion) <= maxBytes
    ? withVersion
    : terminalLine(undefined)
}
